import { format, parse } from 'date-fns';
import { Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  resolveDateOpening,
  useGetLocationSchedule,
} from '@/features/location-opening-hours';
import { useFlagStatus } from '@/lib/use-flag-status';
import { cn } from '@/lib/utils';
import { OVERRIDABILITY_OF_STANDARD_OPERATING_HOURS_FLAG } from '@borradh-workspace/contracts';
import {
  type ResolvedShiftDay,
  type ShiftInterval,
  useDeleteShiftOverride,
  useSetShiftOverride,
} from '../api';
import { formatHoursLabel, hhmmToMinutes, minutesToHHmm } from '../lib/time';
import {
  shiftIntervalsInvalid,
  shiftIntervalsOverlap,
} from './shift-interval-fields';
import { TimeSelect } from './time-select';

interface ShiftOverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  practitionerId: string;
  practitionerName: string;
  /** YYYY-MM-DD */
  date: string;
  /** The currently resolved day (overrides applied), used to seed the form. */
  resolvedDay: ResolvedShiftDay | null;
  locationId: string;
}

/** Suggest the next shift starting after the latest existing one. */
function nextInterval(
  intervals: ShiftInterval[],
  fallback: ShiftInterval
): ShiftInterval {
  if (intervals.length === 0) return fallback;
  const latestEnd = Math.max(...intervals.map((i) => i.endMinutes));
  const start = Math.min(latestEnd + 60, 23 * 60);
  return {
    startMinutes: start,
    endMinutes: Math.min(start + 120, 24 * 60 - 5),
  };
}

/**
 * Edits a single day's shifts for one practitioner. Saving writes a date
 * override (which replaces the weekly pattern for that date). Removing all
 * intervals marks the day off; resetting an override restores scheduled shifts.
 */
export function ShiftOverrideDialog({
  open,
  onOpenChange,
  practitionerId,
  practitionerName,
  date,
  resolvedDay,
  locationId,
}: ShiftOverrideDialogProps) {
  const { schedule, isLoading: isHoursLoading } = useGetLocationSchedule({
    locationId,
    windowStart: date,
    windowEnd: date,
  });
  const opening = resolveDateOpening(
    parse(date, 'yyyy-MM-dd', new Date()),
    schedule
  );
  const defaultStart = opening.fromMinutes;
  const defaultEnd = opening.toMinutes;
  const closed = opening.closed;
  // Flag on: a shift may sit outside the day's hours, and saving opens the
  // location for it (server side, this date only).
  const { enabled: canOverrideHours } = useFlagStatus(
    OVERRIDABILITY_OF_STANDARD_OPERATING_HOURS_FLAG
  );
  const [intervals, setIntervals] = useState<ShiftInterval[]>([]);

  useEffect(() => {
    if (!open || isHoursLoading) return;
    const seeded =
      resolvedDay && !resolvedDay.isOff
        ? resolvedDay.intervals.map((i) => ({
            startMinutes: i.startMinutes,
            endMinutes: i.endMinutes,
          }))
        : [];
    setIntervals(
      seeded.length > 0
        ? seeded
        : closed
          ? []
          : [{ startMinutes: defaultStart, endMinutes: defaultEnd }]
    );
  }, [open, resolvedDay, isHoursLoading, closed, defaultStart, defaultEnd]);

  const { setShiftOverride, isSaving } = useSetShiftOverride({
    onSuccess: () => onOpenChange(false),
  });
  const { deleteShiftOverride, isDeleting } = useDeleteShiftOverride({
    onSuccess: () => onOpenChange(false),
  });

  const hasOverride = resolvedDay?.source === 'override';
  const totalMinutes = intervals.reduce(
    (sum, i) => sum + Math.max(0, i.endMinutes - i.startMinutes),
    0
  );
  const invalid =
    intervals.length > 0 &&
    (shiftIntervalsInvalid(intervals) || shiftIntervalsOverlap(intervals));
  const opensLocation =
    canOverrideHours &&
    !isHoursLoading &&
    intervals.some(
      (i) =>
        closed || i.startMinutes < defaultStart || i.endMinutes > defaultEnd
    );

  const update = (index: number, patch: Partial<ShiftInterval>) =>
    setIntervals((prev) =>
      prev.map((interval, i) =>
        i === index ? { ...interval, ...patch } : interval
      )
    );

  const removeInterval = (index: number) =>
    setIntervals((prev) => prev.filter((_, i) => i !== index));

  const handleSave = () => {
    const isOff = intervals.length === 0;
    setShiftOverride({
      practitionerId,
      locationId,
      date,
      isOff,
      intervals: isOff ? [] : intervals,
    });
  };

  // Delete-day: drop an existing override, or turn a weekly day off for
  // this date via an "off" override.
  const handleDeleteDay = () => {
    if (hasOverride) {
      deleteShiftOverride({ practitionerId, date });
    } else {
      setShiftOverride({
        practitionerId,
        locationId,
        date,
        isOff: true,
        intervals: [],
      });
    }
  };

  const firstName = practitionerName.split(' ')[0] || practitionerName;
  const displayDate = format(
    parse(date, 'yyyy-MM-dd', new Date()),
    'EEE, MMM d'
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="pr-5">
            {firstName}&apos;s shift {displayDate}
          </DialogTitle>
          <DialogDescription>
            You are editing this day&apos;s shifts only. To set repeating
            shifts, go to scheduled shifts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {intervals.length === 0 ? (
            <p className="rounded-lg bg-muted/50 px-4 py-6 text-center text-sm text-muted-foreground">
              Not working this day.
            </p>
          ) : (
            intervals.map((interval, index) => (
              <div key={index} className="flex items-end gap-2 sm:gap-3">
                <div className="min-w-0 flex-1 space-y-1.5">
                  {index === 0 && <Label>Start time</Label>}
                  <TimeSelect
                    className="w-full min-w-0"
                    value={minutesToHHmm(interval.startMinutes)}
                    onChange={(v) =>
                      update(index, { startMinutes: hhmmToMinutes(v) })
                    }
                  />
                </div>
                <div className="min-w-0 flex-1 space-y-1.5">
                  {index === 0 && <Label>End time</Label>}
                  <TimeSelect
                    className="w-full min-w-0"
                    value={minutesToHHmm(interval.endMinutes)}
                    onChange={(v) =>
                      update(index, { endMinutes: hhmmToMinutes(v) })
                    }
                  />
                </div>
                {/* Phones keep the row on one line: just the icon, which
                    still announces "Remove shift". */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-muted-foreground max-sm:size-9 max-sm:px-0"
                  onClick={() => removeInterval(index)}
                >
                  <X />
                  <span className="max-sm:sr-only">Remove shift</span>
                </Button>
              </div>
            ))
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5 rounded-full"
              disabled={isHoursLoading || (closed && !canOverrideHours)}
              onClick={() =>
                setIntervals((prev) => [
                  ...prev,
                  nextInterval(prev, {
                    startMinutes: defaultStart,
                    endMinutes: defaultEnd,
                  }),
                ])
              }
            >
              <Plus className="size-4" />
              Add shift
            </Button>
            {intervals.length > 0 && (
              <span className="text-sm text-muted-foreground">
                Total shift duration: {formatHoursLabel(totalMinutes)}
              </span>
            )}
          </div>

          {intervals.length > 0 && shiftIntervalsOverlap(intervals) && (
            <p className="text-sm text-destructive">Shifts must not overlap.</p>
          )}
          {shiftIntervalsInvalid(intervals) && (
            <p className="text-sm text-destructive">
              Each shift must end after it starts.
            </p>
          )}
          {opensLocation && (
            <p className="text-sm text-muted-foreground">
              Outside opening hours. Saving opens the location for{' '}
              {intervals.length > 1 ? 'these shifts' : 'this shift'} on{' '}
              {displayDate} only.
            </p>
          )}
        </div>

        <DialogFooter className="items-stretch sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            className={cn(
              'rounded-full',
              !hasOverride &&
                'text-destructive hover:bg-destructive/10 hover:text-destructive'
            )}
            disabled={isDeleting || isSaving}
            onClick={handleDeleteDay}
          >
            {hasOverride ? <RotateCcw /> : <Trash2 />}
            {hasOverride ? 'Reset to scheduled shifts' : 'Mark day off'}
          </Button>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="rounded-full"
              disabled={isHoursLoading || isSaving || invalid}
              onClick={handleSave}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
